/*
 * Doverfield Home cart store: the adapter between the theme's cart UI and
 * Shopify's Cart AJAX API (/cart.js, /cart/add.js, /cart/change.js,
 * /cart/update.js, /cart/clear.js).
 *
 * Everything else (cart drawer, cart page, product form, quick add, header
 * badge) talks to `window.DoverfieldCart` or listens for the `cart:updated`
 * event, so this is the only file that knows where the cart lives.
 *
 * Public API (every method returns a Promise that resolves with the full cart):
 *   DoverfieldCart.get()                           -> cart
 *   DoverfieldCart.add({ id, quantity })           -> cart   (also { items: [...] })
 *   DoverfieldCart.change({ id | line, quantity }) -> cart   (id may be a line key)
 *   DoverfieldCart.update({ updates: { [variantId]: quantity } }) -> cart
 *   DoverfieldCart.clear()                         -> cart
 *   DoverfieldCart.subscribe(fn)                   -> unsubscribe function
 *   DoverfieldCart.routeUrl(url)                   -> url (identity on Shopify)
 *   DoverfieldCart.ready                           -> Promise resolved once the cart is loaded
 * Rejections carry `status` (the HTTP status, 422 for stock and quantity
 * problems) and `description`, from Shopify's error body.
 * After every change (and once on load) `document` receives
 * `new CustomEvent('cart:updated', { detail: { cart } })`.
 *
 * The cart object is Shopify's, normalised for the UI:
 *   items[]: key, id / variant_id, product_id, product_title, variant_title
 *            (null for a product with only the default variant), title,
 *            handle, url, image ({ src, alt } or null), price and line_price
 *            (final prices after line discounts, in cents), quantity
 *   item_count, total_price, currency
 *   free_shipping: { threshold, remaining, progress, unlocked } from the
 *                  free shipping threshold theme setting (all 0 when unset)
 *
 * The first cart comes from the JSON the cart-drawer section prints
 * ([data-cart-json]), so the badge and drawer fill without a request.
 * Requests run one at a time, in order, as Shopify recommends.
 */

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Shopify serves root-relative URLs directly, so links need no rewriting. */
export function routeUrl(url) {
  return url;
}

/** Adds a width to a Shopify CDN image URL so cart thumbnails stay small. */
function sizedImage(url, width) {
  if (typeof url !== 'string' || !url) return '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.href;
  } catch {
    return url;
  }
}

function freeShipping(total) {
  const threshold = Math.round(Number(document.documentElement.dataset.freeShippingThreshold) || 0);
  if (threshold <= 0) return { threshold: 0, remaining: 0, progress: 0, unlocked: false };
  return {
    threshold,
    remaining: Math.max(0, threshold - total),
    progress: Math.min(1, total / threshold),
    unlocked: total >= threshold,
  };
}

function normaliseItem(item) {
  const image = item.image || (item.featured_image && item.featured_image.url) || '';
  const defaultOnly = item.product_has_only_default_variant === true
    || String(item.variant_title || '').trim().toLowerCase() === 'default title';
  const price = item.final_price ?? item.price ?? 0;
  return {
    key: String(item.key),
    id: item.variant_id ?? item.id,
    variant_id: item.variant_id ?? item.id,
    product_id: item.product_id ?? null,
    product_title: item.product_title || item.title || '',
    variant_title: defaultOnly ? null : item.variant_title || null,
    title: item.title || item.product_title || '',
    handle: item.handle || null,
    url: item.url || null,
    image: image ? { src: sizedImage(image, 240), alt: (item.featured_image && item.featured_image.alt) || '' } : null,
    price,
    quantity: Number(item.quantity) || 0,
    line_price: item.final_line_price ?? item.line_price ?? price * (Number(item.quantity) || 0),
    original_line_price: item.original_line_price ?? item.line_price ?? price * (Number(item.quantity) || 0),
    // A free gift line (private _gift property), e.g. the Press Pack pump.
    gift: Boolean(item.properties && item.properties._gift),
    // Line item properties shoppers should see, e.g. the Press Pack's Free gift.
    // Keys starting with "_" are private by Shopify convention.
    properties: Object.entries(item.properties || {})
      .filter(([key, value]) => !key.startsWith('_') && value !== null && value !== '')
      .map(([key, value]) => ({ key, value: String(value) })),
  };
}

export function normaliseCart(raw) {
  const data = raw && typeof raw === 'object' ? raw : {};
  const items = Array.isArray(data.items) ? data.items.map(normaliseItem) : [];
  const total = Number(data.total_price) || 0;
  return {
    token: data.token || null,
    items,
    item_count: Number(data.item_count) || items.reduce((sum, item) => sum + item.quantity, 0),
    total_price: total,
    currency: data.currency || 'USD',
    free_shipping: freeShipping(total),
  };
}

// cart.js leaves out compare-at prices, so read them from each product's .js
// (once per handle per page) to show sale lines with the regular price
// crossed out and total the saving. A gift line ($0, no compare-at) adds none.
const compareAtCache = new Map();

async function withCompareAt(cart) {
  const handles = [...new Set(cart.items.map((item) => item.handle))]
    .filter((handle) => handle && !compareAtCache.has(handle));
  await Promise.all(handles.map((handle) => (
    fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((product) => {
        const prices = new Map();
        (product?.variants || []).forEach((v) => prices.set(Number(v.id), Number(v.compare_at_price) || 0));
        compareAtCache.set(handle, prices);
      })
  )));
  let savings = 0;
  cart.items.forEach((item) => {
    const compareAt = compareAtCache.get(item.handle)?.get(Number(item.variant_id)) || 0;
    item.compare_line_price = Math.max(item.original_line_price, compareAt * item.quantity);
    if (!item.gift) savings += Math.max(0, item.compare_line_price - item.line_price);
  });
  cart.savings = savings;
  return cart;
}

class CartRequestError extends Error {
  constructor(status, body) {
    const description = (body && (body.description || body.message)) || 'Cart request failed';
    super(description);
    this.name = 'CartError';
    this.status = status;
    this.description = description;
  }
}

async function request(path, body) {
  const options = body === undefined
    ? { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' }
    : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };
  const response = await fetch(`${root}${path}`, options);
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }
  if (!response.ok) throw new CartRequestError(response.status, json);
  return json;
}

function readInlineCart() {
  const el = document.querySelector('[data-cart-json]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch {
    return null;
  }
}

function domReady() {
  if (document.readyState !== 'loading') return Promise.resolve();
  return new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
}

function createStore() {
  const listeners = new Set();
  let current = null;
  let queue = Promise.resolve();

  const emit = (cart) => {
    current = cart;
    for (const fn of [...listeners]) {
      try {
        fn(cart);
      } catch (err) {
        console.error('[cart] subscriber failed', err);
      }
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  };

  const fetchCart = () => request('cart.js').then(normaliseCart).then(withCompareAt);

  // Runs cart operations one after another; a failed one does not block the next.
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  const ready = (async () => {
    await domReady();
    const inline = readInlineCart();
    if (inline) return emit(await withCompareAt(normaliseCart(inline)));
    try {
      return emit(await fetchCart());
    } catch (err) {
      console.warn('[cart] Could not load the cart', err);
      return emit(normaliseCart(null));
    }
  })();

  // A page restored from the back/forward cache may show an old cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) enqueue(() => fetchCart().then(emit)).catch(() => {});
  });

  const api = {
    get: () => ready.then(() => current),
    add(input = {}) {
      const items = Array.isArray(input.items)
        ? input.items
        : [{ id: input.id, quantity: input.quantity ?? 1, ...(input.properties ? { properties: input.properties } : {}) }];
      // add.js returns only the added items, so read the whole cart after it.
      return ready.then(() => enqueue(() => request('cart/add.js', { items }).then(fetchCart).then(emit)));
    },
    change(input = {}) {
      const body = { quantity: Math.max(0, Math.floor(Number(input.quantity)) || 0) };
      if (input.line !== undefined && input.line !== null) body.line = Number(input.line);
      else body.id = String(input.id);
      return ready.then(() => enqueue(() => request('cart/change.js', body).then(normaliseCart).then(withCompareAt).then(emit)));
    },
    update(input = {}) {
      return ready.then(() => enqueue(() => request('cart/update.js', { updates: input.updates || {} }).then(normaliseCart).then(withCompareAt).then(emit)));
    },
    clear() {
      return ready.then(() => enqueue(() => request('cart/clear.js', {}).then(normaliseCart).then(withCompareAt).then(emit)));
    },
    subscribe(fn) {
      if (typeof fn !== 'function') return () => {};
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    routeUrl,
    ready: null,
  };
  api.ready = ready.then(() => api);

  // A free gift line goes when nothing else of its product is left in the
  // cart (no other variant), or once its offer has ended (data-gift-ends on
  // the cart JSON), so nobody checks out a "free" line at full price. It stays
  // at one, and a $0 line that didn't come in as a gift (the gift-only
  // Presspack Airpump added some other way) goes too.
  // Shopify takes the discount off the first matching line, and the cart
  // lists the newest first, so when a paid line of the same variant (a second
  // pump) lands above the gift, the gift is re-added to sit on top again.
  const giftEnds = Date.parse(document.querySelector('[data-cart-json]')?.dataset.giftEnds || '');
  let pruning = false;
  const pruneGifts = (cart) => {
    if (pruning || !cart) return;
    const ended = Number.isFinite(giftEnds) && Date.now() >= giftEnds;
    const stale = cart.items.filter((gift) => gift.gift && (ended || !cart.items.some((item) => (
      !item.gift && item.product_id === gift.product_id && item.variant_id !== gift.variant_id
    ))));
    cart.items.forEach((item) => {
      if (!item.gift && item.price === 0 && item.original_line_price === 0) stale.push(item);
    });
    const buried = cart.items.filter((gift, index) => gift.gift && !stale.includes(gift)
      && cart.items.slice(0, index).some((item) => !item.gift && item.variant_id === gift.variant_id));
    const extra = cart.items.filter((gift) => gift.gift && gift.quantity > 1 && !stale.includes(gift));
    if (!stale.length && !buried.length && !extra.length) return;
    pruning = true;
    const giftProperties = (gift) => ({
      ...Object.fromEntries(gift.properties.map(({ key, value }) => [key, value])),
      _gift: '1',
    });
    let work = Promise.resolve();
    stale.forEach((gift) => { work = work.then(() => api.change({ id: gift.key, quantity: 0 })); });
    extra.forEach((gift) => { work = work.then(() => api.change({ id: gift.key, quantity: 1 })); });
    buried.forEach((gift) => {
      work = work
        .then(() => api.change({ id: gift.key, quantity: 0 }))
        .then(() => api.add({ items: [{ id: gift.variant_id, quantity: gift.quantity, properties: giftProperties(gift) }] }));
    });
    work.catch(() => {}).finally(() => { pruning = false; });
  };
  listeners.add(pruneGifts);
  const untilEnd = giftEnds - Date.now();
  if (untilEnd > 0 && untilEnd < 7 * 86400 * 1000) {
    setTimeout(() => api.get().then(pruneGifts), untilEnd + 1000);
  }
  return api;
}

const DoverfieldCart = window.DoverfieldCart || createStore();
window.DoverfieldCart = DoverfieldCart;

export default DoverfieldCart;
export { DoverfieldCart };
